import katex from 'katex';
import { splitMath } from '@classroom-ai/types';

// ----------------------------------------------------------------------
// Math → HTML for non-React surfaces (print HTML, TipTap node views), epic #290.
// The notation contract is LaTeX inside `$…$` (display `$$…$$`); `splitMath` decides
// which dollars are delimiters, so currency like "$2.50" stays text.

const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export const escapeHtml = (s: string): string => s.replace(/[&<>"']/g, (c) => ESCAPES[c]);

/** `htmlAndMathml` needs the KaTeX stylesheet; `mathml` renders natively in the
 *  browser with no CSS or fonts — use it for standalone documents (print). */
export type MathOutput = 'htmlAndMathml' | 'mathml';

/** Render one LaTeX expression to KaTeX HTML. Never throws — invalid LaTeX renders as
 *  its source (KaTeX's `throwOnError: false` shows it in the error colour). */
export function latexToHtml(latex: string, display = false, output: MathOutput = 'htmlAndMathml'): string {
  try {
    return katex.renderToString(latex, { displayMode: display, throwOnError: false, output });
  } catch {
    return escapeHtml(latex);
  }
}

/**
 * Render a plain string with `$…$` spans to HTML: math is KaTeX, text goes through
 * `formatText`, which must escape it (default: HTML-escape). Math is swapped for placeholders while the text
 * is formatted, so inline Markdown like `**bold $x$ text**` can wrap a math span.
 */
export function mathTextToHtml(
  text: string,
  opts: { output?: MathOutput; formatText?: (raw: string) => string } = {}
): string {
  const segs = splitMath(text);
  if (!segs.some((x) => x.kind === 'math')) return (opts.formatText ?? escapeHtml)(text);
  const rendered: string[] = [];
  const PH = String.fromCharCode(0xe001);
  const withPlaceholders = segs
    .map((seg) => {
      if (seg.kind === 'text') return seg.value;
      rendered.push(latexToHtml(seg.value, seg.display, opts.output));
      return `${PH}${rendered.length - 1}${PH}`;
    })
    .join('');
  return (opts.formatText ?? escapeHtml)(withPlaceholders).replace(new RegExp(`${PH}(\\d+)${PH}`, 'g'), (_, i) => rendered[Number(i)]);
}
